import { Link } from 'react-router-dom'

import { FxButton, FxIcon, FxPanel } from '@/components/fx'
// Not re-exported by `components/share/index.ts` — it is written as a part
// of the share dialog. Deep import rather than a copy; the index should
// export it, reported.
import { UploadLinkSection } from '@/components/share/UploadLinkSection'
import { useBlobUrl } from '@/hooks/useBlobUrl'
import { useFavorites } from '@/lib/favorites'
import { formatThreshold } from '@/pages/einstellungen/defaultThreshold'
import type { CockpitState } from '../useCockpit'
import { editorPath } from '../template/templateTabModel'
import { microsToDate } from './documentsModel'

/**
 * The 290px column on the left of the Dokumente tab: what this form *is*,
 * beside everything that has been uploaded against it.
 *
 * Two panels, as the artboard draws them. The first is the form's identity
 * card — its first page, the way into the editor, how many fields of each
 * kind it has and the one-line summary of its settings. The second is
 * „Per Handy hinzufügen“, which is `UploadLinkSection` verbatim: the same
 * panel the share dialog shows, mounted here because this is where the
 * artboard puts it and because it is otherwise the only way to get an upload
 * token out of the running app — and `apps/capture` exists to serve exactly
 * those tokens.
 *
 * The counts come from `cockpit.fields`, i.e. from the template's *shared*
 * fields where it has them, so a field placed on the 2018 and the 2019
 * layout is one field here as it is one column in the export.
 */
export function TemplateAside({ cockpit }: { cockpit: CockpitState }) {
  const { template, templateId, fields, canWrite } = cockpit
  // The page image, not `template.imagePath`: the gateway sets `thumbnailPath`
  // (which the client maps onto `imagePath`) only on the *list* responses —
  // `GET /v1/templates/:id` carries no thumbnail at all, so reading it here
  // meant every cockpit showed "Keine Seitenansicht verfügbar." The artboard
  // draws the page itself rather than the 320px thumbnail anyway, and the
  // detail payload has it on every page.
  const previewPage =
    cockpit.layouts[0] ?? template?.pageList?.find((page) => page.variantOf == null) ?? template?.pageList?.[0]
  const { url } = useBlobUrl(previewPage?.imagePath || template?.imagePath || null)
  const { favorites, toggle } = useFavorites()

  // The editor opens on a page **number**, which is not an index into
  // `pageList`: numbers are never compacted, so a deleted first page leaves
  // `pageList[0].page === 2`. The first primary page is the one to open —
  // a variant is an alternative layout of another page, not a page to start
  // on.
  const firstPage = cockpit.layouts[0]?.page ?? template?.pageList?.[0]?.page

  return (
    <aside className="flex w-full flex-col gap-6">
      <FxPanel className="flex flex-col gap-4 p-5">
        <div className="relative rounded-sm bg-fill px-4 pb-4 pt-3.5">
          {/* Only the bookmark. The artboard also draws a „Gesperrt“ lock,
              but nothing in the gateway says a template is locked, and a
              badge that is always absent is a promise we would be inventing.
              The star is device-local (see `lib/favorites`), same as on the
              Übersicht's cards. */}
          <div className="mb-2 flex justify-end">
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded-sm bg-surface text-primary-text"
              aria-pressed={favorites.has(templateId)}
              onClick={() => toggle(templateId)}
            >
              <FxIcon name="bookmark" size={18} filled={favorites.has(templateId)} />
              <span className="sr-only">Als Favorit merken</span>
            </button>
          </div>
          {url ? (
            <img
              src={url}
              alt={`Erste Seite von „${template?.title ?? 'diesem Musterformular'}“`}
              className="mx-auto block max-h-[260px] w-full object-contain bg-surface shadow-raised"
            />
          ) : (
            // No guessed sheet of ruled lines here: an image that is still
            // loading and one the gateway will not serve look the same, and
            // a drawn placeholder reads as "this is your form".
            <p className="m-0 py-10 text-center text-small text-text-muted">
              Keine Seitenansicht verfügbar.
            </p>
          )}
        </div>

        {canWrite && firstPage != null && (
          <FxButton variant="tonal" icon="edit" block asChild>
            <Link to={editorPath(templateId, firstPage)}>Vorlage bearbeiten</Link>
          </FxButton>
        )}

        <ul className="m-0 grid list-none grid-cols-3 gap-2 p-0">
          <FieldTile icon="stylus_note" count={fields.text} label="Text" />
          <FieldTile icon="disabled_by_default" count={fields.checkbox} label="Checkbox" />
          <FieldTile icon="signature" count={fields.signature} label="Signatur" />
        </ul>

        <p className="m-0 text-small text-text">{metaLine(cockpit)}</p>
      </FxPanel>

      {/* `UploadLinkSection` is written for the share dialog, where it sits
          under a rule as the last of several sections. Here it is the whole
          panel, so its own top rule is suppressed rather than the component
          copied. It wants a `className` of its own; reported. */}
      <FxPanel className="p-5 [&>section]:border-t-0 [&>section]:pt-0">
        <UploadLinkSection templateId={templateId} canWrite={canWrite} />
      </FxPanel>
    </aside>
  )
}

function FieldTile({ icon, count, label }: { icon: string; count: number; label: string }) {
  return (
    <li className="flex flex-col items-center gap-0.5 rounded-sm bg-ground px-1 py-2.5">
      <FxIcon name={icon} size={22} className="text-primary" />
      <b className="text-[20px] leading-none">{count}</b>
      <span className="text-[13px]">{label}</span>
    </li>
  )
}

const dateFormat = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

/**
 * „1 Seite · 2 Layouts · Schwellwert 80 % · geändert 17.09.2026“.
 *
 * Each part is dropped when the template does not carry it — `updatedAt` is
 * absent on templates written before the column existed, and a date invented
 * from `createdAt` would be a wrong answer stated confidently. Seiten counts
 * the primary pages, Layouts every page including the variants, which is the
 * distinction the rest of this tab makes too.
 */
export function metaLine(cockpit: CockpitState): string {
  const pages = cockpit.layouts.length
  const layouts = cockpit.template?.pageList?.length ?? 0
  const parts = [pages === 1 ? '1 Seite' : `${pages} Seiten`]
  if (layouts > pages) parts.push(layouts === 1 ? '1 Layout' : `${layouts} Layouts`)
  if (cockpit.template) {
    parts.push(`Schwellwert ${formatThreshold(cockpit.template.confidenceThreshold)}`)
  }
  const changed = microsToDate(cockpit.template?.updatedAt)
  if (changed) parts.push(`geändert ${dateFormat.format(changed)}`)
  return parts.join(' · ')
}
